/**
 * FikoDimensionInteraction - Sürükle-Bırak Etkileşim Yöneticisi
 *
 * Dimension objelerinin mouse/touch ile etkileşimini yönetir:
 * - Sürükle-bırak (drag-and-drop)
 * - Hover highlight
 * - Seçim (click)
 * - Bounding box tabanlı hit detection
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { FikoInteractiveDimension, DimensionSegment } from './FikoInteractiveDimension';
import { FikoMeasurementDisplay } from './FikoMeasurementDisplay';
import { FikoGDTSymbol } from './FikoGDTSymbol';

// ============================================================================
// TYPES
// ============================================================================

export type DraggableObject = FikoInteractiveDimension | FikoMeasurementDisplay | FikoGDTSymbol | THREE.Group;

export interface InteractionConfig {
    /** Sürükleme aktif mi */
    enableDrag: boolean;
    /** Hover efektleri aktif mi */
    enableHover: boolean;
    /** Seçim aktif mi */
    enableSelect: boolean;
    /** Sürükleme hassasiyeti (piksel) */
    dragThreshold: number;
    /** Hover highlight rengi */
    hoverColor: number;
    /** Seçim highlight rengi */
    selectColor: number;
    /** Grid snap değeri (null = snap yok) */
    snapToGrid: number | null;
    /** Hit detection genişletme (piksel) */
    hitPadding: number;
    /** Sürükleme sınırları */
    bounds: {
        min: THREE.Vector3;
        max: THREE.Vector3;
    } | null;
}

export interface DragState {
    isDragging: boolean;
    target: DraggableObject | null;
    segment: DimensionSegment | null;
    startPosition: THREE.Vector3;
    startMousePosition: THREE.Vector2;
    currentPosition: THREE.Vector3;
    offset: THREE.Vector3;
    hasMoved: boolean;
}

export interface InteractionEvent {
    type: 'dragstart' | 'drag' | 'dragend' | 'select' | 'hover' | 'hoverend';
    target: DraggableObject;
    segment?: DimensionSegment;
    position: THREE.Vector3;
    delta?: THREE.Vector3;
    originalEvent: MouseEvent | TouchEvent | PointerEvent;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_INTERACTION_CONFIG: InteractionConfig = {
    enableDrag: true,
    enableHover: true,
    enableSelect: true,
    dragThreshold: 5,
    hoverColor: 0x00ff88,
    selectColor: 0xffaa00,
    snapToGrid: null,
    hitPadding: 15,
    bounds: null
};

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoDimensionInteraction - Dimension etkileşim yöneticisi
 */
export class FikoDimensionInteraction {
    // Referanslar
    private camera: THREE.Camera;
    private domElement: HTMLElement;
    private scene: THREE.Scene;

    // Konfigürasyon
    private config: InteractionConfig;

    // Yönetilen objeler - Map ile bounding box cache
    private objects: Map<DraggableObject, THREE.Box3> = new Map();

    // Durum
    private dragState: DragState = {
        isDragging: false,
        target: null,
        segment: null,
        startPosition: new THREE.Vector3(),
        startMousePosition: new THREE.Vector2(),
        currentPosition: new THREE.Vector3(),
        offset: new THREE.Vector3(),
        hasMoved: false
    };

    private hoveredObject: DraggableObject | null = null;
    private selectedObject: DraggableObject | null = null;
    private isEnabled: boolean = true;

    // Raycaster ve yardımcılar
    private raycaster: THREE.Raycaster = new THREE.Raycaster();
    private mouse: THREE.Vector2 = new THREE.Vector2();
    private mousePixel: THREE.Vector2 = new THREE.Vector2();

    // Plane for dragging
    private dragPlane: THREE.Plane = new THREE.Plane();
    private intersection: THREE.Vector3 = new THREE.Vector3();

    // Temp vectors
    private tempVec3: THREE.Vector3 = new THREE.Vector3();
    private tempBox: THREE.Box3 = new THREE.Box3();

    // Event callbacks
    public onDragStart?: (event: InteractionEvent) => void;
    public onDrag?: (event: InteractionEvent) => void;
    public onDragEnd?: (event: InteractionEvent) => void;
    public onSelect?: (event: InteractionEvent) => void;
    public onHover?: (event: InteractionEvent) => void;
    public onHoverEnd?: (event: InteractionEvent) => void;

    // Bound event handlers
    private boundOnPointerDown: (e: PointerEvent) => void;
    private boundOnPointerMove: (e: PointerEvent) => void;
    private boundOnPointerUp: (e: PointerEvent) => void;

    constructor(
        camera: THREE.Camera,
        domElement: HTMLElement,
        scene: THREE.Scene,
        config: Partial<InteractionConfig> = {}
    ) {
        this.camera = camera;
        this.domElement = domElement;
        this.scene = scene;
        this.config = { ...DEFAULT_INTERACTION_CONFIG, ...config };

        // Raycaster ayarları - Line için threshold artır
        this.raycaster.params.Line = { threshold: 5 };
        (this.raycaster.params as any).Line2 = { threshold: 10 };

        // Bind event handlers
        this.boundOnPointerDown = this.onPointerDown.bind(this);
        this.boundOnPointerMove = this.onPointerMove.bind(this);
        this.boundOnPointerUp = this.onPointerUp.bind(this);

        // Event listener'ları ekle
        this.attachEventListeners();
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Yönetilecek obje ekle
     */
    public add(object: DraggableObject): void {
        const box = new THREE.Box3();
        this.updateBoundingBox(object, box);
        this.objects.set(object, box);
    }

    /**
     * Objeyi kaldır
     */
    public remove(object: DraggableObject): void {
        this.objects.delete(object);
        if (this.hoveredObject === object) {
            this.clearHover(object);
            this.hoveredObject = null;
        }
        if (this.selectedObject === object) {
            this.selectedObject = null;
        }
        if (this.dragState.target === object) {
            this.cancelDrag();
        }
    }

    /**
     * Tüm objeleri kaldır
     */
    public clear(): void {
        this.objects.clear();
        this.hoveredObject = null;
        this.selectedObject = null;
        this.cancelDrag();
    }

    /**
     * Aktif/Pasif yap
     */
    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        if (!enabled) {
            this.cancelDrag();
            if (this.hoveredObject) {
                this.clearHover(this.hoveredObject);
                this.hoveredObject = null;
            }
        }
    }

    /**
     * Sürüklemeyi iptal et
     */
    public cancelDrag(): void {
        if (this.dragState.isDragging && this.dragState.target) {
            // Pozisyonu geri al
            this.dragState.target.position.copy(this.dragState.startPosition);
        }
        this.resetDragState();
    }

    /**
     * Seçili objeyi al
     */
    public getSelected(): DraggableObject | null {
        return this.selectedObject;
    }

    /**
     * Objeyi seç
     */
    public select(object: DraggableObject | null): void {
        if (this.selectedObject && this.selectedObject !== object) {
            this.clearSelection(this.selectedObject);
        }
        this.selectedObject = object;
        if (object) {
            this.highlightSelection(object);
        }
    }

    /**
     * Seçimi temizle
     */
    public deselect(): void {
        if (this.selectedObject) {
            this.clearSelection(this.selectedObject);
            this.selectedObject = null;
        }
    }

    /**
     * Bounding box'ları güncelle (obje değiştiğinde çağır)
     */
    public updateBounds(): void {
        this.objects.forEach((box, obj) => {
            this.updateBoundingBox(obj, box);
        });
    }

    /**
     * Konfigürasyonu güncelle
     */
    public setConfig(config: Partial<InteractionConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Kamerayı güncelle
     */
    public updateCamera(camera: THREE.Camera): void {
        this.camera = camera;
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.detachEventListeners();
        this.objects.clear();
        this.hoveredObject = null;
        this.selectedObject = null;
    }

    // ========================================================================
    // PRIVATE - EVENT LISTENERS
    // ========================================================================

    private attachEventListeners(): void {
        // 🎯 FIX: Capture phase kullanarak diğer handler'lardan önce yakala
        this.domElement.addEventListener('pointerdown', this.boundOnPointerDown, { passive: false, capture: true });
        this.domElement.addEventListener('pointermove', this.boundOnPointerMove, { passive: false, capture: true });
        this.domElement.addEventListener('pointerup', this.boundOnPointerUp, { passive: false, capture: true });
        this.domElement.addEventListener('pointerleave', this.boundOnPointerUp, { passive: false, capture: true });
        this.domElement.addEventListener('pointercancel', this.boundOnPointerUp, { passive: false, capture: true });
    }

    private detachEventListeners(): void {
        // 🎯 FIX: capture: true ile eklendiyse, kaldırırken de capture: true olmalı
        this.domElement.removeEventListener('pointerdown', this.boundOnPointerDown, { capture: true });
        this.domElement.removeEventListener('pointermove', this.boundOnPointerMove, { capture: true });
        this.domElement.removeEventListener('pointerup', this.boundOnPointerUp, { capture: true });
        this.domElement.removeEventListener('pointerleave', this.boundOnPointerUp, { capture: true });
        this.domElement.removeEventListener('pointercancel', this.boundOnPointerUp, { capture: true });
    }

    // ========================================================================
    // PRIVATE - POINTER EVENTS
    // ========================================================================

    private onPointerDown(event: PointerEvent): void {
        if (!this.isEnabled) return;
        if (event.button !== 0) return; // Sadece sol tık

        this.updateMouse(event);
        const hit = this.hitTest();

        if (hit && this.config.enableDrag) {
            // 🎯 FIX: Event'i tamamen durdur - FikoSecililer'e ulaşmasın
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            // Capture pointer
            this.domElement.setPointerCapture(event.pointerId);

            this.startDrag(hit.object, hit.point, event);
        }
    }

    private onPointerMove(event: PointerEvent): void {
        if (!this.isEnabled) return;

        this.updateMouse(event);

        if (this.dragState.isDragging) {
            // 🎯 FIX: Sürükleme aktifken event'i durdur
            event.preventDefault();
            event.stopPropagation();
            this.updateDrag(event);
        } else if (this.config.enableHover) {
            this.updateHover(event);
        }
    }

    private onPointerUp(event: PointerEvent): void {
        if (!this.isEnabled) return;

        // Release pointer capture
        if (this.domElement.hasPointerCapture(event.pointerId)) {
            this.domElement.releasePointerCapture(event.pointerId);
        }

        if (this.dragState.isDragging) {
            // 🎯 FIX: Sürükleme aktifken event'i tamamen durdur
            // Bu sayede FikoSecililer'in tiklandi event'i tetiklenmez
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            // Hareket ettiyse drag end, etmediyse click/select
            if (this.dragState.hasMoved) {
                this.endDrag(event);
            } else {
                // Click olarak işle - target'ı kaydet çünkü resetDragState null yapacak
                const clickTarget = this.dragState.target;
                this.resetDragState();
                if (this.config.enableSelect && clickTarget) {
                    this.handleSelect(clickTarget, event);
                }
            }
        }
    }

    // ========================================================================
    // PRIVATE - MOUSE UTILITIES
    // ========================================================================

    private updateMouse(event: PointerEvent): void {
        const rect = this.domElement.getBoundingClientRect();

        // Normalized coordinates (-1 to 1)
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Pixel coordinates
        this.mousePixel.x = event.clientX - rect.left;
        this.mousePixel.y = event.clientY - rect.top;
    }

    // ========================================================================
    // PRIVATE - HIT TESTING (Bounding Box tabanlı)
    // ========================================================================

    private hitTest(): { object: DraggableObject; point: THREE.Vector3 } | null {
        // Raycaster'ı ayarla
        this.raycaster.setFromCamera(this.mouse, this.camera);

        let closestObject: DraggableObject | null = null;
        let closestDistance = Infinity;
        let closestPoint = new THREE.Vector3();

        // Her objeyi kontrol et
        this.objects.forEach((box, obj) => {
            if (!obj.visible) return;

            // Bounding box'ı güncelle
            this.updateBoundingBox(obj, box);

            // Box'ı biraz genişlet (hit padding)
            this.tempBox.copy(box);
            const padding = this.config.hitPadding * this.getWorldScale();
            this.tempBox.expandByScalar(padding);

            // Ray ile kesişim kontrolü
            const intersects = this.raycaster.ray.intersectsBox(this.tempBox);

            if (intersects) {
                // En yakın noktayı bul
                this.raycaster.ray.intersectBox(this.tempBox, this.tempVec3);
                const distance = this.raycaster.ray.origin.distanceTo(this.tempVec3);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestObject = obj;
                    closestPoint.copy(this.tempVec3);
                }
            }
        });

        if (closestObject) {
            return {
                object: closestObject,
                point: closestPoint
            };
        }

        return null;
    }

    /**
     * World space scale hesapla (kamera uzaklığına göre)
     */
    private getWorldScale(): number {
        // Kamera mesafesine göre scale
        if (this.camera instanceof THREE.PerspectiveCamera) {
            // Ortalama obje pozisyonuna mesafe
            return this.camera.position.length() * 0.001;
        }
        if (this.camera instanceof THREE.OrthographicCamera) {
            return (this.camera.top - this.camera.bottom) * 0.01;
        }
        return 1;
    }

    /**
     * Objenin bounding box'ını güncelle
     */
    private updateBoundingBox(object: DraggableObject, box: THREE.Box3): void {
        box.makeEmpty();

        object.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.geometry) {
                    mesh.geometry.computeBoundingBox();
                    if (mesh.geometry.boundingBox) {
                        this.tempBox.copy(mesh.geometry.boundingBox);
                        this.tempBox.applyMatrix4(mesh.matrixWorld);
                        box.union(this.tempBox);
                    }
                }
            } else if ((child as THREE.Sprite).isSprite) {
                const sprite = child as THREE.Sprite;
                sprite.getWorldPosition(this.tempVec3);
                const scale = Math.max(sprite.scale.x, sprite.scale.y) * 0.5;
                this.tempBox.setFromCenterAndSize(
                    this.tempVec3,
                    new THREE.Vector3(scale, scale, scale)
                );
                box.union(this.tempBox);
            } else if ((child as THREE.Line).isLine) {
                const line = child as THREE.Line;
                if (line.geometry) {
                    line.geometry.computeBoundingBox();
                    if (line.geometry.boundingBox) {
                        this.tempBox.copy(line.geometry.boundingBox);
                        this.tempBox.applyMatrix4(line.matrixWorld);
                        box.union(this.tempBox);
                    }
                }
            }
        });

        // Minimum boyut garantisi
        if (box.isEmpty()) {
            object.getWorldPosition(this.tempVec3);
            box.setFromCenterAndSize(this.tempVec3, new THREE.Vector3(10, 10, 10));
        }
    }

    // ========================================================================
    // PRIVATE - DRAG OPERATIONS
    // ========================================================================

    private startDrag(object: DraggableObject, point: THREE.Vector3, event: PointerEvent): void {
        this.dragState.isDragging = true;
        this.dragState.target = object;
        this.dragState.startPosition.copy(object.position);
        this.dragState.startMousePosition.copy(this.mouse);
        this.dragState.currentPosition.copy(point);
        this.dragState.offset.copy(point).sub(object.position);
        this.dragState.hasMoved = false;

        // Drag plane oluştur (kameraya dik, objenin pozisyonundan geçen)
        const cameraDirection = new THREE.Vector3();
        this.camera.getWorldDirection(cameraDirection);
        this.dragPlane.setFromNormalAndCoplanarPoint(cameraDirection.negate(), object.position);

        // Cursor değiştir
        this.domElement.style.cursor = 'grabbing';

        // Callback
        if (this.onDragStart) {
            this.onDragStart({
                type: 'dragstart',
                target: object,
                position: point.clone(),
                originalEvent: event
            });
        }
    }

    private updateDrag(event: PointerEvent): void {
        if (!this.dragState.target) return;

        // Hareket threshold kontrolü
        const dx = this.mouse.x - this.dragState.startMousePosition.x;
        const dy = this.mouse.y - this.dragState.startMousePosition.y;
        const moved = Math.sqrt(dx * dx + dy * dy) * 500; // Normalize to pixels approx

        if (moved < this.config.dragThreshold && !this.dragState.hasMoved) {
            return; // Henüz yeterince hareket etmedi
        }

        this.dragState.hasMoved = true;

        // Ray'i plane ile kesiştir
        this.raycaster.setFromCamera(this.mouse, this.camera);
        if (!this.raycaster.ray.intersectPlane(this.dragPlane, this.intersection)) return;

        // Yeni pozisyon hesapla
        const newPosition = this.intersection.clone().sub(this.dragState.offset);

        // Grid snap
        if (this.config.snapToGrid !== null) {
            const snap = this.config.snapToGrid;
            newPosition.x = Math.round(newPosition.x / snap) * snap;
            newPosition.y = Math.round(newPosition.y / snap) * snap;
            newPosition.z = Math.round(newPosition.z / snap) * snap;
        }

        // Bounds kontrolü
        if (this.config.bounds) {
            newPosition.clamp(this.config.bounds.min, this.config.bounds.max);
        }

        // Delta hesapla
        const delta = newPosition.clone().sub(this.dragState.target.position);

        // Pozisyonu güncelle
        this.dragState.target.position.copy(newPosition);
        this.dragState.currentPosition.copy(this.intersection);

        // Callback
        if (this.onDrag) {
            this.onDrag({
                type: 'drag',
                target: this.dragState.target,
                position: newPosition.clone(),
                delta: delta,
                originalEvent: event
            });
        }
    }

    private endDrag(event: PointerEvent): void {
        if (!this.dragState.target) return;

        const target = this.dragState.target;
        const finalPosition = target.position.clone();

        // Cursor geri al
        this.domElement.style.cursor = this.hoveredObject ? 'grab' : 'default';

        // Bounding box güncelle
        const box = this.objects.get(target);
        if (box) {
            this.updateBoundingBox(target, box);
        }

        // Callback
        if (this.onDragEnd) {
            this.onDragEnd({
                type: 'dragend',
                target: target,
                position: finalPosition,
                originalEvent: event
            });
        }

        this.resetDragState();
    }

    private resetDragState(): void {
        this.dragState.isDragging = false;
        this.dragState.target = null;
        this.dragState.segment = null;
        this.dragState.offset.set(0, 0, 0);
        this.dragState.hasMoved = false;
        this.domElement.style.cursor = this.hoveredObject ? 'grab' : 'default';
    }

    // ========================================================================
    // PRIVATE - HOVER
    // ========================================================================

    private updateHover(event: PointerEvent): void {
        const hit = this.hitTest();

        if (hit) {
            if (this.hoveredObject !== hit.object) {
                // Eski hover'ı temizle
                if (this.hoveredObject) {
                    this.clearHover(this.hoveredObject);
                    if (this.onHoverEnd) {
                        this.onHoverEnd({
                            type: 'hoverend',
                            target: this.hoveredObject,
                            position: new THREE.Vector3(),
                            originalEvent: event
                        });
                    }
                }

                // Yeni hover
                this.hoveredObject = hit.object;
                this.highlightHover(hit.object);
                this.domElement.style.cursor = 'grab';

                if (this.onHover) {
                    this.onHover({
                        type: 'hover',
                        target: hit.object,
                        position: hit.point,
                        originalEvent: event
                    });
                }
            }
        } else if (this.hoveredObject) {
            // Hover'dan çıktı
            this.clearHover(this.hoveredObject);
            if (this.onHoverEnd) {
                this.onHoverEnd({
                    type: 'hoverend',
                    target: this.hoveredObject,
                    position: new THREE.Vector3(),
                    originalEvent: event
                });
            }
            this.hoveredObject = null;
            this.domElement.style.cursor = 'default';
        }
    }

    // ========================================================================
    // PRIVATE - SELECT
    // ========================================================================

    private handleSelect(object: DraggableObject, event: PointerEvent): void {
        // Toggle seçim
        if (this.selectedObject === object) {
            this.deselect();
        } else {
            this.select(object);

            if (this.onSelect) {
                this.onSelect({
                    type: 'select',
                    target: object,
                    position: object.position.clone(),
                    originalEvent: event
                });
            }
        }
    }

    // ========================================================================
    // PRIVATE - HIGHLIGHT
    // ========================================================================

    private highlightHover(object: DraggableObject): void {
        // FikoInteractiveDimension için segment hover
        if (object instanceof FikoInteractiveDimension) {
            object.hoverSegment(DimensionSegment.LABEL);
        }

        // Generic glow efekti
        this.applyHighlight(object, this.config.hoverColor, 0.3);
    }

    private clearHover(object: DraggableObject): void {
        if (object instanceof FikoInteractiveDimension) {
            object.hoverSegment(null);
        }

        this.removeHighlight(object);
    }

    private highlightSelection(object: DraggableObject): void {
        if (object instanceof FikoInteractiveDimension) {
            object.selectSegment(DimensionSegment.LABEL);
        }

        this.applyHighlight(object, this.config.selectColor, 0.5);
    }

    private clearSelection(object: DraggableObject): void {
        if (object instanceof FikoInteractiveDimension) {
            object.selectSegment(null);
        }

        this.removeHighlight(object);
    }

    /**
     * Highlight efekti uygula
     */
    private applyHighlight(object: THREE.Object3D, color: number, intensity: number): void {
        object.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (mesh.isMesh && mesh.material) {
                const mat = mesh.material as THREE.MeshBasicMaterial;
                if (!mat.userData) mat.userData = {};

                // Orijinal rengi sakla
                if (mat.color && mat.userData.__originalColor === undefined) {
                    mat.userData.__originalColor = mat.color.getHex();
                }

                // Highlight rengi blend et
                if (mat.color) {
                    const original = new THREE.Color(mat.userData.__originalColor || 0xffffff);
                    const highlight = new THREE.Color(color);
                    mat.color.copy(original).lerp(highlight, intensity);
                }
            }

            const line = child as THREE.Line;
            if (line.isLine && line.material) {
                const mat = line.material as THREE.LineBasicMaterial;
                if (!mat.userData) mat.userData = {};

                if (mat.color && mat.userData.__originalColor === undefined) {
                    mat.userData.__originalColor = mat.color.getHex();
                }

                if (mat.color) {
                    const original = new THREE.Color(mat.userData.__originalColor || 0xffffff);
                    const highlight = new THREE.Color(color);
                    mat.color.copy(original).lerp(highlight, intensity);
                }
            }

            const sprite = child as THREE.Sprite;
            if (sprite.isSprite && sprite.material) {
                const mat = sprite.material as THREE.SpriteMaterial;
                if (!mat.userData) mat.userData = {};

                if (mat.userData.__originalOpacity === undefined) {
                    mat.userData.__originalOpacity = mat.opacity;
                }

                // Opacity artır
                mat.opacity = Math.min(1, (mat.userData.__originalOpacity || 1) + intensity);
            }
        });
    }

    /**
     * Highlight efektini kaldır
     */
    private removeHighlight(object: THREE.Object3D): void {
        object.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (mesh.isMesh && mesh.material) {
                const mat = mesh.material as THREE.MeshBasicMaterial;
                if (mat.userData?.__originalColor !== undefined && mat.color) {
                    mat.color.setHex(mat.userData.__originalColor);
                }
            }

            const line = child as THREE.Line;
            if (line.isLine && line.material) {
                const mat = line.material as THREE.LineBasicMaterial;
                if (mat.userData?.__originalColor !== undefined && mat.color) {
                    mat.color.setHex(mat.userData.__originalColor);
                }
            }

            const sprite = child as THREE.Sprite;
            if (sprite.isSprite && sprite.material) {
                const mat = sprite.material as THREE.SpriteMaterial;
                if (mat.userData?.__originalOpacity !== undefined) {
                    mat.opacity = mat.userData.__originalOpacity;
                }
            }
        });
    }
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Hızlı interaction manager oluştur
 */
export function createDimensionInteraction(
    camera: THREE.Camera,
    domElement: HTMLElement,
    scene: THREE.Scene,
    objects?: DraggableObject[]
): FikoDimensionInteraction {
    const manager = new FikoDimensionInteraction(camera, domElement, scene);

    if (objects) {
        objects.forEach(obj => manager.add(obj));
    }

    return manager;
}

/**
 * Grid snap ile interaction manager oluştur
 */
export function createSnapInteraction(
    camera: THREE.Camera,
    domElement: HTMLElement,
    scene: THREE.Scene,
    gridSize: number = 5
): FikoDimensionInteraction {
    return new FikoDimensionInteraction(camera, domElement, scene, {
        snapToGrid: gridSize
    });
}

/**
 * Bounds ile interaction manager oluştur
 */
export function createBoundedInteraction(
    camera: THREE.Camera,
    domElement: HTMLElement,
    scene: THREE.Scene,
    min: THREE.Vector3,
    max: THREE.Vector3
): FikoDimensionInteraction {
    return new FikoDimensionInteraction(camera, domElement, scene, {
        bounds: { min, max }
    });
}
